import { Events } from '../events/app-events';
import { requires } from '../mixins/dependency';
import { eventBus } from '../utils/event-bus';
import { ApiService } from './api-service';
import { BaseService } from './base-service';
import { QuestionsService } from './questions-service';
import { PopupService } from './popup-service';
import { BillingData } from '@ahoy/types/billing';
import { TierData, WebSectionTrackingData } from '@ahoy/types';
import { Admiral, Captain, Skipper, Customized } from '../views/icons/icons';
import { Question } from '../model/question';
import { TemplateResult } from 'lit-html';
import { t, substituteUploadLinks, setPendingOrDraftDocumentTypesProvider } from '@APP/utils/locale';
import { defer } from '../utils/async-store';
import { capitalizeFirstLetter } from '@ahoy/model/dist/utils/capitalizeFirstLetter';
import axios from 'axios';
import { ConfigService } from './config-service';
import { deepCopy } from '@APP/utils/funcs';

type Deps = {
  apiService: ApiService;
  questionsService: QuestionsService;
  popupService: PopupService;
  configService: ConfigService
};

export type getBankNameData = {
  routingNumber: string,
  accountNumber: string;
};

export type IncludedCoverageItem = {
  text: string | undefined,
  spanText: string | undefined;
  astrixText: string | undefined;
};

@requires(
  ApiService.serviceName,
  QuestionsService.serviceName,
  PopupService.serviceName,
  ConfigService.serviceName,
)
export class InsuranceService extends BaseService<Deps> {
  static serviceName = 'insuranceService';

  private rateResult = new Map<string, any>();
  private parallelRequestsCount = 0;
  private unsub: Function | undefined = void 0;
  private unsubSection: Function | undefined = void 0;
  private approvedUWApprovals: string[] = [];
  // Document types (e.g., 'Survey', 'Bill of Sale') for Customer_Document
  // records linked to this application in status 'Pending UW' or 'Draft'.
  // Populated from /insure/get-uw-approvals so callers can tell whether a
  // doc-driven UW error already has an in-progress upload.
  private pendingOrDraftDocumentTypes: string[] = [];
  private didInitUWApprovals = false;

  /** Change in these fields will request rate in packages */
  private baseRateFields =
    ['owner_name',
      'manufacture_year',
      'boatType',
      'length',
      'numberOfEngine',
      'horsepower',
      'value',
      'overrideValue',
      'motorType',
      'dieselFuel',
      'owner_zip_code',
      'state',
      'is_garaged',
      'garage_state',
      'garage_zip_code',
      'gender',
      'marital_status',
      'Number_of_Owners',
      'Driver_License_Last_Name',
      'driver_birth_date',
      'driving_license_state',
      'driving_license_number',
      'has_driving_license',
      'boating_experience',
      'is_trailer_insured',
      'is_additional_boaters',
      'additional_boaters_info',
      'Is_Package_Customized',
      'Layup1_From',
      'Layup1_To',
      'Layup2_From',
      'Layup2_To',
      'is_layup',
      'otherBoatModel',
    ];
  /** Change in these fields will request rate in billing and customization */
  private Sub2RateFields = [
    'Physical_Damage_Settlement_option',
    'boat_purchase_date',
    'COLL_Coverage_Deductible_YN',
    'COLL_Coverage_Deductible_Amount',
    'COMP_Coverage_Deductible_YN',
    'COMP_Coverage_Deductible_Amount',
    'BI_Bodily_Injury_Liability_YN',
    'BI_Limit',
    'PD_Property_Damage_Liability_YN',
    'PD_Limit_per_occurrence',
    'MP_Medical_Payments_YN', 'MP_Limit_per_occurrence',
    'UBUIBBI_Uninsured_Underinsured_Boater_YN',
    'UBUBBI_Limit',
    'TL_On_Water_Towing_YN', 'TL_Limit_per_occurrence',
    'PEREF_Personal_Effects_YN',
    'PEREF_Limit_per_occurrence',
    'FSHEQ_Fishing_Equipment_YN',
    'FSHEQ_Limit_per_occurrence',
    'RSA_Roadside_Assistance_YN',
    'RENTRE_Rental_Reimbursement_YN',
    'UBUIBPD_Limit_YN',
    'UBUIBPD_Limit',
    'Family_Bodily_Injury_YN',
    'insurance_start_date',
    'MTA_start_date',
  ];



  constructor() {
    super();
    if (!this.unsub) {
      this.unsub = eventBus().on(Events.Type.QuestionUpdate, this.onQuestionUpdate);
    }
    if (!this.unsubSection) {
      this.unsubSection = eventBus().on(Events.Type.SectionChanged, this.onSectionChanged);
    }
    // Let locale.ts read the latest in-progress doc types so substituteUploadLinks
    // can append the "customer has already provided <X>" note.
    setPendingOrDraftDocumentTypesProvider(() => this.pendingOrDraftDocumentTypes);
  }

  private setApprovedUWApprovals(arr: string[]) {
    this.approvedUWApprovals = arr || [];
    this.deps.questionsService.setApprovedUWApprovals(this.approvedUWApprovals);
  }

  private setPendingOrDraftDocumentTypes(arr: string[]) {
    this.pendingOrDraftDocumentTypes = arr || [];
  }

  public getPendingOrDraftDocumentTypes(): string[] {
    return this.pendingOrDraftDocumentTypes;
  }

  // pure read-from-cached-rate refresh; safe to call on cache hits (no API call, no model writes)
  private refreshApprovedFromRate(rateResult: any, stage: string) {
    const approved = rateResult?.validateUnderWritingApprovalsErrorData?.underwriting_Approvals_Pending_And_Rejected?.underwriting_Approvals_Approved;
    if (!approved) return;
    this.setApprovedUWApprovals(approved);
    console.log(`[InsuranceService] ${stage} cache-hit refresh — approvedUWApprovals:`, this.approvedUWApprovals);
  }

  private onSectionChanged = async ({ sectionId }: { sectionId: string }) => {
    if (this.didInitUWApprovals) return;

    const sections = this.deps.questionsService.model.getSections();
    const currentIdx = sections.findIndex((s: any) => s.sectionId === sectionId);
    const sBoatIdx = sections.findIndex((s: any) => s.sectionId === 'S_Boat');
    // only initialize when S_Boat exists in the layout and is at-or-before the current section
    if (sBoatIdx === -1 || currentIdx === -1 || sBoatIdx > currentIdx) return;

    this.didInitUWApprovals = true;

    const questionnaireId = this.deps.questionsService.questionnaireId;
    if (!questionnaireId) {
      console.log(`[InsuranceService] funnel start (current=${sectionId}) — no questionnaireId yet, skipping fetch`);
      this.didInitUWApprovals = false;
      return;
    }

    try {
      const response = await this.deps.apiService.http(`/insure/get-uw-approvals?questionnaireId=${questionnaireId}`);
      const data = response?.data;
      if (!data) throw new Error('Empty response from /insure/get-uw-approvals');
      this.setApprovedUWApprovals(data.Approved || []);
      this.setPendingOrDraftDocumentTypes(data.PendingOrDraftDocumentTypes || []);
      console.log(`[InsuranceService] funnel start (current=${sectionId}) — approvedUWApprovals (api):`, this.approvedUWApprovals, 'pendingOrDraftDocumentTypes:', this.pendingOrDraftDocumentTypes);
    } catch (err) {
      console.warn(`[InsuranceService] funnel start (current=${sectionId}) — /insure/get-uw-approvals failed, falling back:`, err);
      const cached = this.rateResult.get('billing') || this.rateResult.get('packages');
      const cachedApproved = cached?.validateUnderWritingApprovalsErrorData?.underwriting_Approvals_Pending_And_Rejected?.underwriting_Approvals_Approved;
      if (cachedApproved) {
        this.setApprovedUWApprovals(cachedApproved);
        console.log(`[InsuranceService] funnel start (current=${sectionId}) — approvedUWApprovals (rate cache):`, this.approvedUWApprovals);
      } else {
        console.log(`[InsuranceService] funnel start (current=${sectionId}) — approvedUWApprovals (kept previous):`, this.approvedUWApprovals);
      }
    }
  };

  public async completeMTA() { 
    try {
      const insuranceServiceResponse = await this.MTA();
      if (insuranceServiceResponse?.success) {
        this.deps.popupService.showPopup('mta-complete-popup', '', 'Close Window');
        return { success: true };
      }
      else {
        const supportPhoneNumber = this.deps.questionsService.model.getSupportPhoneNumberText();
        await this.deps.questionsService.showErrorsAsPopup(t('UW_Error_34', supportPhoneNumber));
        return { success: false, error: t('UW_Error_34') };
      }
    } catch {
      return { success: false, error: t('UW_Error_34') };
    }
  }

  private onQuestionUpdate = (question: Question) => {
    if (this.baseRateFields.includes(question.questionId)) {
      this.rateResult = new Map<string, any>();
    }
    if (this.Sub2RateFields.includes(question.questionId)) {
      this.rateResult.set('billing', null);
    }
  };

  private getBillingRateFromQuestionnaire() {

    const watercraftName = this.deps.questionsService.model.getQuestionById('watercraft_name')!.value as string || '';
    const manufacturerName = capitalizeFirstLetter(this.deps.questionsService.model.getQuestionById('make')!.value as string) || '';
    const model = capitalizeFirstLetter(this.deps.questionsService.model.getQuestionById('model')!.value as string) || '';
    const { questionsService } = this.deps;
    const billingData: BillingData = {
      'full-amount-annual': questionsService.model.getQuestionById('Total_Premium_Annual')?.value as number,
      'full-amount-monthly': questionsService.model.getQuestionById('Total_Premium_Monthly')?.value as number,
      'number-of-installments': questionsService.model.getQuestionById('Installments_Number')?.value as number,
      'MTA_Premium_Yearly_Amount': questionsService.model.getQuestionById('MTA_Premium_Yearly_Amount')?.value as number,
      'MTA_Premium_Monthly_Amount': questionsService.model.getQuestionById('MTA_Premium_Monthly_Amount')?.value as number,
      'Last_Written_Premium': questionsService.model.getQuestionById('Last_Written_Premium')?.value as number,
      'credit-fee': questionsService.model.getQuestionById('Installment_Fee_CC')?.value as number,
      'ach-fee': questionsService.model.getQuestionById('Installment_Fee_ACH')?.value as number,
      'monthly-down-payment': questionsService.model.getQuestionById('Down_Payment_Amount')?.value as number,
      'monthly-payment': questionsService.model.getQuestionById('Installment_Amount')?.value as number,
      'Premium_ProRata': questionsService.model.getQuestionById('Premium_ProRata')?.value as number,
      'Last_Premium': questionsService.model.getQuestionById('Last_Premium')?.value as number,
      'BoatsData': [{
        'appId': questionsService.model.getQuestionnaireId(),
        'boatName': watercraftName || manufacturerName + ' ' + model,
        'boatStatus': 'Complete',
        'package': {
          'Package': questionsService.model.getQuestionById('package')?.value as string,
          'Premium': {
            'annual-amount': questionsService.model.getQuestionById('Total_Premium_Annual')?.value as number,
            'monthly-amount': questionsService.model.getQuestionById('Total_Premium_Monthly')?.value as number,
          },
          'Is_Package_Customized': questionsService.model.getQuestionById('Is_Package_Customized')?.value == '$_BOOL_true',
          'Physical_Damage_Settlement_option': questionsService.model.getQuestionById('Physical_Damage_Settlement_option')?.value as string,
          'COLL_Coverage_Deductible_Amount': questionsService.model.getQuestionById('COLL_Coverage_Deductible_Amount')?.value as number,
          'COMP_Coverage_Deductible_Amount': questionsService.model.getQuestionById('COMP_Coverage_Deductible_Amount')?.value as number,
          'BI_Limit_per_occurrence': questionsService.model.getQuestionById('BI_Limit_per_occurrence')?.value as number,
          'BI_Limit_per_person': questionsService.model.getQuestionById('BI_Limit_per_person')?.value as number,
          'PD_Limit_per_occurrence': questionsService.model.getQuestionById('PD_Limit_per_occurrence')?.value as number,
          'MP_Limit_per_occurrence': questionsService.model.getQuestionById('MP_Limit_per_occurrence')?.value as number,
          'UBUBBI_Limit_per_occurrence': questionsService.model.getQuestionById('UBUBBI_Limit_per_occurrence')?.value as number,
          'UBUBBI_Limit_per_person': questionsService.model.getQuestionById('UBUBBI_Limit_per_person')?.value as number,
          'TL_Limit_per_occurrence': questionsService.model.getQuestionById('TL_Limit_per_occurrence')?.value as number,
          'FSHEQ_Limit_per_occurrence': questionsService.model.getQuestionById('FSHEQ_Limit_per_occurrence')?.value as number,
          'PEREF_Limit_per_occurrence': questionsService.model.getQuestionById('PEREF_Limit_per_occurrence')?.value as number,
          'BI_Bodily_Injury_Liability': questionsService.model.getQuestionById('BI_Bodily_Injury_Liability_YN')?.value == '$_BOOL_true',
          'PD_Property_Damage_Liability': questionsService.model.getQuestionById('PD_Property_Damage_Liability_YN')?.value == '$_BOOL_true',
          'MP_Medical_Payments': questionsService.model.getQuestionById('MP_Medical_Payments_YN')?.value == '$_BOOL_true',
          'UBUIBBI_Uninsured_Underinsured_Boater': questionsService.model.getQuestionById('UBUIBBI_Uninsured_Underinsured_Boater_YN')?.value == '$_BOOL_true',
          'TL_On_Water_Towing': questionsService.model.getQuestionById('TL_On_Water_Towing_YN')?.value == '$_BOOL_true',
          'PEREF_Personal_Effects': questionsService.model.getQuestionById('PEREF_Personal_Effects_YN')?.value == '$_BOOL_true',
          'FSHEQ_Fishing_Equipment': questionsService.model.getQuestionById('FSHEQ_Fishing_Equipment_YN')?.value == '$_BOOL_true',
          'RSA_Roadside_Assistance': questionsService.model.getQuestionById('RSA_Roadside_Assistance_YN')?.value == '$_BOOL_true',
          'UBUIBPD': questionsService.model.getQuestionById('UBUIBPD_Limit_YN')?.value == '$_BOOL_true',
          'UBUIBPD_Limit': questionsService.model.getQuestionById('UBUIBPD_Limit')?.value as number,
          'Family_Bodily_Injury': questionsService.model.getQuestionById('Family_Bodily_Injury_YN')?.value == '$_BOOL_true',
          'RENTRE_Rental_Reimbursement': questionsService.model.getQuestionById('RENTRE_Rental_Reimbursement_YN')?.value == '$_BOOL_true',
        },
      }],
      'stage': 'billing',
      'discountData': {
        'affiliate': questionsService.model.getQuestionById('Affiliate')?.value as string,
        'originalRate': questionsService.model.getQuestionById('Rate_Before_Discount')?.value as number,
        'discountPercentage': questionsService.model.getQuestionById('Discount_Percentage')?.value as number,
        'discountAmount': questionsService.model.getQuestionById('Discount_Amount')?.value as number,
        'rateAfterDiscount': questionsService.model.getQuestionById('Rate_After_Discount')?.value as number,
      },
    };
    return billingData;
  }


  private async waitForRateComplete(): Promise<void> {
    this.setParallelRequestsCount(this.parallelRequestsCount + 1);
    if (this.parallelRequestsCount > 1) {
      return defer.recycle('insurance-service-parallel-rate-requests');
    } else {
      return Promise.resolve();
    }
  }

  private setParallelRequestsCount(newValue: number) {
    this.parallelRequestsCount = newValue;
    if (this.parallelRequestsCount == 0) {
      defer.recycle('insurance-service-parallel-rate-requests').resolve(true);
    }
  }

  public clearLastError(stage: string) {
    this.rateResult.set(stage, null);
  }

  async downloadQuoteUrl(id: string): Promise<{ data: Blob, filename: string; }> {
    const endPoint = this.deps.configService.get('lighthouseApiEndpoint') as string + '/' + (this.deps.configService.get('apiPrefix'));

    const response = await axios.get<Blob>(endPoint + '/quote/' + id, {
      responseType: 'blob',
    });
    let filename = `Quote_${id}.pdf`;
    const m = response.headers['content-disposition']?.match(/filename\*?=(UTF-8'')?(.+)/);
    if (m) {
      filename = m[2];
    }

    return { data: response.data, filename: filename };
  }

  async downloadQuote(questionnaireId: string) {
    if (!questionnaireId) return;
    const { apiService } = this.deps;
    try {
      apiService.lock();
      const file = await this.downloadQuoteUrl(questionnaireId);
      const url = window.URL.createObjectURL(file.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      apiService.unlock();
    }
  }

  public getRecommendedPackage() {
    return 'Tier 2';
  }

  private async isDirectOrIndirectUser() {
    const checkDirectIndirectResponse = await this.deps.questionsService.checkDirectIndirect();
    const isDirect = checkDirectIndirectResponse?.Type === 'Direct';
    return isDirect;
  }

  private async getUWApprovalsErrorMessage({ rateResult, isDirect }: { rateResult: any, isDirect: boolean }) {
    let textResult:string[] = [];
    // now we need to determine the text to be displayed in the popup according to the pending and rejected underwriting approvals
    // if we have any uw approvals in status rejected, we need to display a list of them first
    // if not, and we have uw approvals in status pending, we need to display a list of them
    // anyway, all pending uw approvals except for UW_Error_40 have the same message
    // UW_Error_40 has a different message, depending on if the user is direct or indirect
    // same logic for rejected uw approvals. We return the UNIQUE of the messages array, to avoid
    // displaying duplicates (e.g. 2 same Rejected message for 2 non-UW-Error-40 Uw approvals in status rejected)
    const rejectedUWApprovals = (rateResult?.validateUnderWritingApprovalsErrorData?.underwriting_Approvals_Pending_And_Rejected?.Rejected || [])
      .filter((item: string) => item !== 'customer_document_rejected' && item !== 'customer_document_pending');
    const pendingUWApprovals = (rateResult?.validateUnderWritingApprovalsErrorData?.underwriting_Approvals_Pending_And_Rejected?.Pending || [])
      .filter((item: string) => item !== 'customer_document_rejected' && item !== 'customer_document_pending');


    // handle rejected uw approvals
    if (rejectedUWApprovals.length > 0) {
      if (!rejectedUWApprovals.includes('UW_Error_40')) {
        // we have no UW_Error_40, so we can use the default rejected message
        textResult.push(t('Underwriting_Status_Text_Rejected'));
      }
      // we have UW_Error_40 with another rejected uw approval
      else if (rejectedUWApprovals.length > 1) {
        textResult.push(t('Underwriting_Status_Text_Rejected'));
        textResult.push('$_BULLET');
        textResult.push(t('Underwriting_Status_Text_Rejected_40'));
        // add Rejected_Header_Text to be used as the popup header value
        // this is the case where we display the popup on the payment screen with bullet points
        // and custom header text
        return `Rejected_Header_Text::${textResult.join('. ')}`;
      }
      // we have UW_Error_40 with no other rejected uw approval
      else {
        textResult.push(t('Underwriting_Status_Text_Rejected_40'));
      }
      // return a string, after removing the dot on the end of all array elements but the last
      for (let i = 0; i < textResult.length - 1; i++) {
        textResult[i] = textResult[i].replace(/\.$/, '');
      }
      return textResult.join('. ');
    }
    
    // handle 
    // pending uw approvals

     if (pendingUWApprovals.length > 0) {

      let specialCustomerErrors = ['UW_Error_46', 'UW_Error_35'];
      let specialAgentErrors = ['UW_Error_46A', 'UW_Error_35A'];
      let hasSpecialCustomerError = false
      let hasSpecialAgentError = false
      let hasGeneralError = false;
      let hasError40 = false;
      let hasUW_Error_37:boolean = false;
      let hasUW_Error_56:boolean = false;
      let hasUW_Error_51:boolean = false;

      for (const error of pendingUWApprovals) {
        if (error === 'UW_Error_40') {
          hasError40 = true;
        } else if (specialCustomerErrors.includes(error)) {
          hasSpecialCustomerError = true;
        } else if (specialAgentErrors.includes(error)) {
          hasSpecialAgentError = true;
        } else if (error === 'UW_Error_37') {
          hasUW_Error_37 = true;
        } else if (error === 'UW_Error_56') {
          hasUW_Error_56 = true;
        } else if (error === 'UW_Error_51') {
          hasUW_Error_51 = true;
        } else {
          hasGeneralError = true;
        }
      }

      if (hasError40) {
        textResult.push(
          isDirect ? t('Underwriting_Status_Text_Pending_Direct') : t('Underwriting_Status_Text_Pending_Indirect')
        );
        textResult.push('$_BULLET');
      }
      if (hasSpecialCustomerError) {
        textResult.push(t('UW_Error_46'));
        textResult.push('$_BULLET');
      }
      if (hasUW_Error_37) {
        textResult.push(t('UW_Error_37'));
        textResult.push('$_BULLET');
      }
      if (hasUW_Error_56) {
        textResult.push(t('UW_Error_56'));
        textResult.push('$_BULLET');
      }
      if (hasUW_Error_51) {
        textResult.push(t('UW_Error_51'));
        textResult.push('$_BULLET');
      }
      if (hasSpecialAgentError) {
        textResult.push(t('UW_Error_46A'));
        textResult.push('$_BULLET');
      }
      if (hasGeneralError) {
        textResult.push(t('Underwriting_Status_Text_Pending'));
        textResult.push('$_BULLET');
      }

      // remove trailing bullet
      if (textResult[textResult.length - 1] === '$_BULLET') {
        textResult.pop();
      }

      // same formatting as original code
      for (let i = 0; i < textResult.length - 1; i++) {
        textResult[i] = textResult[i].replace(/\.$/, '');
      }

      // Apply upload-link substitution at the composition point so a popup
      // covering multiple pending UW approvals (e.g. UW_Error_51 + UW_Error_37)
      // gets ONE link with both document types, not one link per error. The
      // helper filters pendingUWApprovals against its own UW-error→doc-type
      // map so non-document errors (e.g. UW_Error_40) are safely ignored.
      return substituteUploadLinks(textResult.join('. '), pendingUWApprovals);
    }
    return '';
  }

  private async validateUWApprovals({ rateResult, stage }: {rateResult: any, stage: string}) {
    try {
      const isDirect = await this.isDirectOrIndirectUser();
      // we used to get this value from the zoho questionnaire after it was populated by Underwriting Approval handler
      // in captain. we then modified it to improve performance to get is back in the rate response
      this.deps.questionsService.model.getQuestionById('Underwriting_Approvals_Cleared')!.value = rateResult?.validateUnderWritingApprovalsErrorData?.underwriting_Approvals_Pending_And_Rejected?.isClearedUnderwritingApprovals ? '$_BOOL_true' : '$_BOOL_false';
      this.deps.questionsService.model.getQuestionById('Underwriting_Status_Text')!.value = await this.getUWApprovalsErrorMessage({ rateResult, isDirect });
      // refresh the cached approved UW approvals from the freshly-returned rate (covers packages and billing stages)
      const refreshedApproved = rateResult?.validateUnderWritingApprovalsErrorData?.underwriting_Approvals_Pending_And_Rejected?.underwriting_Approvals_Approved;
      if (refreshedApproved) {
        this.setApprovedUWApprovals(refreshedApproved);
        console.log(`[InsuranceService] rate ${stage} complete — approvedUWApprovals (rate refresh):`, this.approvedUWApprovals);
      }
      // if UW_Error_40 is included in the Pending UW Approvals, we show a popup requesting a Boat Survey on the Packages stage
      if (stage === 'packages' && rateResult?.validateUnderWritingApprovalsErrorData?.underwriting_Approvals_Pending_And_Rejected?.Pending?.includes('UW_Error_40')) {
        const { apiService } = this.deps;
        apiService.unlock();
        await this.deps.questionsService.showErrorsAsPopup(isDirect ? t('UW_Error_40_Direct') : t('UW_Error_40_Indirect'));
      }
    } catch  {
      throw new Error('Error getting uw approvals status');
    }
  }

  public async getLastRate(stage: string, lockScreen: boolean = true) {
    const { apiService } = this.deps;
    const isFromNewCheckout = this.deps.questionsService.isFromCCpayment && this.deps.questionsService.model.getLeftSection() == 'S_Billing';

    if (lockScreen) {
      if (stage === 'packages') apiService.lock(t('M_Loader_Rate'));
      else apiService.lock();
    }

    try {
      await this.waitForRateComplete();
      // isFromNewCheckout = true means that the customer is coming back from checkout
      // without completing the payment, probably because of a failed payment
      // or that he wants to change the payment method. In this case we want to load
      // the last rate from zoho, since we want the price to be the same as in the checkout
      if (stage == 'billing' && isFromNewCheckout) {
        const rateResult = this.getBillingRateFromQuestionnaire();
        this.rateResult.set(stage, rateResult);
        return rateResult;
      }

      //clear billing cache in order to force billing rate after going back to packages
      if (stage == 'packages') {
        this.rateResult.set('billing', null);
      }

      if (this.rateResult.get(stage)) {
        const cached = this.rateResult.get(stage);
        this.refreshApprovedFromRate(cached, stage);
        return cached;
      } else {
        const rateResult = await this.requestRate(stage, lockScreen);
        if (!Array.isArray(rateResult)) {
          this.rateResult.set(stage, rateResult);
        }
        // rateResult?.validateUnderWritingApprovalsErrorData?.underwriting_Approvals_Pending_And_Rejected?.Pending
        await this.validateUWApprovals({ rateResult, stage });
        return rateResult;
      }
    } catch {
      return {};
    } finally {
      // update the questionnaire with the uw_approval status
      this.setParallelRequestsCount(0);
      if (lockScreen) {
        apiService.unlock();
      }
    }
  }

  async sendWebtrackingData({ operation, errorMessage }: Pick<WebSectionTrackingData, 'operation' | 'errorMessage'>) {
    const model = this.deps.questionsService.model;
    const currentSection = model.getActiveSectionName();
    const next_section = currentSection && model.getSectionAfter(currentSection) || '';
    let data: WebSectionTrackingData = {
      section: currentSection || '',
      next_section,
      operation,
      errorMessage,
    };
    eventBus().emit(Events.Type.SaveWebSectionTracking, data);
  };

  public async requestRate(stage: string, lockScreen: boolean = true) {
    const { apiService } = this.deps;
    if (lockScreen) {
      if (stage === 'packages') apiService.lock(t('M_Loader_Rate'));
      else apiService.lock();
    }
    await this.deps.questionsService.flushPatchQueue();
    // Recompute the claims summary before every rate request (new business + renewal, not MTA)
    // so Number_of_Claims / Last_Claim_Date are fresh in Zoho before the offer is rated.
    if (!this.deps.questionsService.isMta()) await this.deps.questionsService.fetchClaimsSummary();
    let rateResult: any;
    try {
      const { questionsService } = this.deps;
      const { watercraft_name } = questionsService.getQuestionsValues(['watercraft_name']);
      const isPaymentLinkMode = questionsService.getPaymentLinkMode();
      const disputedData = questionsService.getDisputeIssuesData();
      if (questionsService.getDisputeIssuesFound() && disputedData?.err) {
        throw [disputedData.err];
      }
      const checkDispute = this.deps.questionsService.getDisputeIssuesFound() === null;
      rateResult = (await this.deps.apiService.http(`/insure/request-offer?stage=${stage}&questionnaireId=${this.deps.questionsService.questionnaireId}&watercraft_name=${watercraft_name}&isPaymentLinkMode=${isPaymentLinkMode}&checkDispute=${checkDispute}`)).data;
      // the rateResult.err is filled on the server side in case of validation error
      if ((rateResult.err || Array.isArray(rateResult))) {
        // add web section tracking
        this.sendWebtrackingData({ operation: 'rate', errorMessage: rateResult.err });
        // On Server validation error reload the reload will show the server_error in left_section 
        //show error if not preload from attestation 
        if (lockScreen) {
          if (rateResult.section) {
            const isPaymentLinkMode = this.deps.questionsService.getPaymentLinkMode();
            // In payment link mode, we want to avoid reloading the page.
            // A reload would exit payment link mode and treat the session as a regular user refresh,
            // since ahoy-auth and ahoy-data cookies are already set (user is logged in).
            // This would allow the user to edit the app, which is not desired.
            // Instead, we should display a non-closable "Hit a Wave" popup to block further interaction.
            if (isPaymentLinkMode) {
              this.deps.questionsService.handleError(rateResult.err || rateResult);
            } else {
              eventBus().emit(Events.Type.GoToWebsite, { url: window.location.href });
            }
          } else {
            // display technical error popup
            this.deps.questionsService.handleError(rateResult.err || rateResult);
            if (Array.isArray(rateResult.err) && rateResult.err.includes('UW_MIN_PACKAGE_VALUE')) {
              return ['UW_MIN_PACKAGE_VALUE'];
            }
          }
        }
        return [];
      }
    } catch (err: any) {
      if (err instanceof Array && (err.includes('UW_Error_42') || err.includes('UW_Error_43'))) { 
        this.deps.questionsService.handleError(err);
        return err;
      }
      // display technical error popup if not preload from attestations
      if (lockScreen) {
        this.deps.questionsService.handleError(err);
      }
      return [];
    } finally {
      if (lockScreen) {
        apiService.unlock();
      }
    }
    return rateResult;
  }

  public async congratsPopup() {
    await this.deps.popupService.showPopup( 'congrats-popup', 'S_Payment', '', 'confety' );
  }

  getPackageNameAndIcon(tier: string): TierData | undefined {
    if (!tier) return;
    const tierNumber = tier.charAt(tier.length - 1);
    let name = t('Package_Tier_' + tierNumber);
    let icon: TemplateResult<2>;
    switch (tierNumber) {
      case '1':
        icon = Skipper;
        return { name, icon };
      case '2':
        icon = Captain;
        return { name, icon };
      case '3':
        icon = Admiral;
        return { name, icon };
      case '4':
        name = t('T_Customized_Package');
        icon = Customized;
        return { name, icon };
    }
    return;
  }

  // checks if it's a complete renewal operation
  private checkIsCompleteRenewalOperation({ op }: any) {
    return op === 'complete-renewal';
  }
  
  /**
   * This function checks if the response from complete renewal operation is valid
   * @param renewalResponse the response object. Success response should be { response: "Subscription created successfully" }
   * @returns boolean
   * */
  public checkValidCompleteRenewalResponse({ renewalResponse }: any) {
    const objToCompare = { response: 'Subscription created successfully' };
    return JSON.stringify(renewalResponse) === JSON.stringify(objToCompare);

  }

  async makeRequest({ data, token, op }:{ token?:any, data?: any, op?: string }) {
    const isCompleteRenewalOperation = this.checkIsCompleteRenewalOperation({ op });
    const applicationId = this.deps.questionsService.questionnaireId ?? data.applicationId;
    const { apiService } = this.deps;
    apiService.lock();
    let result: any;
    try {
      /** If it's a complete renewal operation, call the complete renewal endpoint, else call the billing endpoint */
      result = isCompleteRenewalOperation?(await apiService.http('/insure/renewal/complete', { applicationId })) :(await apiService.http('/insure/billing', { data, token, op }));
      result = result?.data || result?.error || ''; 
    } catch (e) {
      return e;
    } finally {
      apiService.unlock();
    }
    return result;
  }

  async MTA() {
    const { apiService } = this.deps;
    apiService.lock();
    let result: any;
    try {
      result = (await apiService.http('/insure/BindRequest', { questionnaireId: this.deps.questionsService.questionnaireId }))?.data ?? '';
    } catch (e) {
      return e;
    } finally {
      apiService.unlock();
    }
    return result;
  }

  async Renewal() {
    const { apiService } = this.deps;
    apiService.lock();
    let result: any;
    try {
      result = (await apiService.http('/insure/bindRenewalRequest', { questionnaireId: this.deps.questionsService.questionnaireId }))?.data ?? '';
    } catch (e) {
      return e;
    } finally {
      apiService.unlock();
    }
    return result;
  }

  async getBankName(data?: getBankNameData) {
    const { apiService } = this.deps;
    let requestData = data || '';
    let result: any;
    try {
      result = (await apiService.http('/insure/billing/ach', { data: requestData })) ?? '';
    } catch (e) {
      return e;
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  }

  // this was the original function to log the error. now we use it only if the error message has not been logged in the past hour
  private async logCCerrorRequest(data?: any) {
    let requestData = data || '';
    const { apiService } = this.deps;
    let result: any;
    try {
      result = (await apiService.http('/insure/billing-error', { data: requestData })) ?? '';
    } catch (e) {
      return e;
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  }

  // the data is the error message which is a string. we need to alert only once for the same error message
  // so we'll create a map of the error messages and the time they were logged. if the error message is the same
  // and current timestamp is less than 1 hour from the last time it was logged, we'll ignore it.
  public async logCCerror(data?: string): Promise<any> {
    const { model } = this.deps.questionsService;
    const mapQuestion = model.getQuestionById('Urgent_Error_Alerts_Counter');
    // current timestamp
    const errorLogMap: Map<string, number> = mapQuestion?.value ? new Map(JSON.parse(mapQuestion?.value as any)) : new Map();
    // The data is the error message, which is a string
    const requestData = data || '';    
    const now = Date.now();
    const oneHourAgo = now - 3600000; // 1 hour in milliseconds
    // Check if the error message has been logged in the past hour
    const lastLoggedTime = errorLogMap.get(requestData);
    if (lastLoggedTime && lastLoggedTime > oneHourAgo) {
    // notify tracking
      const notifyData: any = {
        errorMessage: requestData,
        notifyStream: 'logRocketAlerts',
      };
      eventBus().emit(Events.Type.NotifyTracking, notifyData);
      return;
    }

    // Log the current error message and update the map
    let result: any;
    try {
      result = await this.logCCerrorRequest(requestData);
      errorLogMap.set(requestData, now);
      // save the map in the model as string
      const mapAsJson = JSON.stringify([...errorLogMap]);
      if (mapQuestion) mapQuestion.value = (mapAsJson) as any;
    } catch (e) {
      return e;
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  }


  // private delayCustomerPoll() {
  //   if (this.isCustomerPolling) {
  //     return;
  //   }
  //   if (this.customerStatusTimeout) {
  //     clearTimeout(this.customerStatusTimeout);
  //   }
  //   setTimeout(() => this.pollCustomerStatus(), 1000);
  // }

  // private customerStatusTimeout: number | undefined;
  // private isCustomerPolling = false;

  // public async pollCustomerStatus() {
  //   this.isCustomerPolling = true;
  //   const response = await this.deps.apiService.http('/insure/customer-check');
  //   this.isCustomerPolling = false;
  //   if (response.data?.result) {
  //     eventBus().emit(Events.Type.CustomerStatusChange);
  //     return;
  //   }
  //   else this.delayCustomerPoll();
  // }

  public getCheckViewCondition = ({ questionsService }:any) => {
    return  (entry: any) => {
      const numberIfNumberlike = (source: string | number): string | number => {
        const asNumber = typeof source === 'string' ? Number(source) : NaN;
        if (isNaN(asNumber)) return source;
        return asNumber;
      };

      const operators = {
        equal: (a: any, b: any) => a === b,
        equals: (a: any, b: any) => a === b,
        notEqual: (a: any, b: any) => a !== b && a,
        notEquals: (a: any, b: any) => a !== b,
        lessThan: (a: number, b: number) => a < b,
        greaterThan: (a: number, b: number) => a > b,
        greaterThanOrEqual: (a: number, b: number) => a >= b,
        lessThanOrEqual: (a: number, b: number) => a <= b,
        olderThanYears: (a: string, years: number) => {
          const now = new Date();
          const then = new Date();
          then.setFullYear(Number(a));
          const diff = 1000 * 60 * 60 * 24 * 365 * years;
          return (now.valueOf() - then.valueOf() > diff && a != '' && a != null);
        },
      } as any;

      // exaple: rule::Q_Is_the_insured_the_owner_of_vessel::equal::Yes
      const createGenericRule = (raw: string)=> {
        if (!raw.startsWith('rule::')) {
          throw new Error('This is not a generic rule' + raw);
        }
        const expression = raw.slice(6);
        const [affectingQuestionId, operator, comparable] = expression.split('::');
        if (!affectingQuestionId || !operator || typeof comparable === 'undefined') {
          throw new Error(`Could not compile expression ${expression}`);
        }
        const op = operators[operator];
        if (!op) {
          throw new Error(`Invalid operator ${operator}`);
        }

        // try to convert to number value
        return {
          affectingQuestionId,
          op,
          comparable: numberIfNumberlike(comparable),
        };
      };

      const qId = entry.viewConditionQid;
      if (!qId) return true;
      const question = questionsService.model.getQuestionById(qId);
      if (!question) return true;
      const conditions = question.viewCondition;
      if (!conditions) return true;
      const rules = conditions && conditions.split(' && ');
      const handlers = rules && rules.map((rule: any) => createGenericRule(rule));
      console.log({ handlers });
      return Array.isArray(handlers)&&handlers.every((handler:any) =>
        handler.op(
          numberIfNumberlike(questionsService.model.getQuestionById(handler.affectingQuestionId)?.value as any),
          handler.comparable,
        ),
      );  
    };
  };

  public includedCoverages: Array<{ id: number; title: string; tooltip: string; value: string; includedCoverage: boolean; astrixText?: string }> = [
    { id: 1, title: t('T_Phone_Overboard'), tooltip: t('T_Phone_Overboard_desc'), value: 'Included', includedCoverage: true },
    { id: 2, title: t('T_Safe_Navigation'), tooltip: t('T_Safe_Navigation_desc'), value: 'Included', includedCoverage: true },
    { id: 3, title: t('T_Stolen_Boat'), tooltip: t('T_Stolen_Boat_desc'), value: 'Included', includedCoverage: true
      // , astrixText: t('T_Stolen_Boat_Astrix_Text') 
    },
    { id: 4, title: t('T_Mobile_App'), tooltip: t('T_Mobile_App_desc'), value: 'Included', includedCoverage: true },
  ];

  public rowsMaster = ({ tier }: any): any => {
    const calculatedState = this.deps.questionsService.model.getCheckByState();
    
    const addStateSpecificRows = ({ stateSpecificRows, rows, calculatedState }: any) => {
      const stateSpecificRowsArr = stateSpecificRows[calculatedState];
      if (stateSpecificRowsArr) {
        stateSpecificRowsArr.forEach((el: any) => {
          rows.splice(el.positionToAdd, 0, el.addedCoverage);
        });
      }
    };

    // this differs from the state specific rows in that it is based on the value of a question
    const addQuestionSpecificRows = ({ questionSpecificRows, rows, questionIdArr }: any) => {
      questionIdArr.forEach((questionId: string) => {
        const questionSpecificRowsArr = questionSpecificRows[questionId];
        if (questionSpecificRowsArr) {
          questionSpecificRowsArr.forEach((el: any) => {
            const isQuestionValueValidForUpdate = this.deps.questionsService.model.getQuestionById(questionId)?.value === el.questionValueToAddCoverageOn;
            if (isQuestionValueValidForUpdate) rows.splice(el.positionToAdd, 0, el.addedCoverage);
          });
        }
      });
    };
    
    // this checks if the calculatedState is in the V2 states and adds the rows if it is.
    const addVersionSpecificRows = ({ versionSpecificRows, rows, versionId, calculatedState }: any) => {
      const versionSpecificRowsArr = versionSpecificRows[versionId];
      if (versionSpecificRowsArr) {
        versionSpecificRowsArr.forEach((el: any) => {
          if (el.dontAddOnStates && el.dontAddOnStates.includes(calculatedState)) {
            el.addedCoverage.value = 'Excluded';
          };
          rows.splice(el.positionToAdd, 0, el.addedCoverage);
        });
      }
    };
    
    //this is needed to avoid changing the original array, used in faq
    const includedCoverages = deepCopy(this.includedCoverages);
    const addIdProperty = ({ rows }: any) => {
      rows.forEach((el: any, i: number) => {
        el.id = i + 1;
      });
    };
  
    const rows = [
      { title: t('Q_Physical_Damage_Settlement_option'), tooltip: '', value: tier?.Physical_Damage_Settlement_option },
      { title: t('Q_COLL_Coverage_Deductible_Amount'), tooltip: t('T_Collision_desc'), value: tier?.COLL_Coverage_Deductible_Amount },
      { title: t('Q_COMP_Coverage_Deductible_Amount'), tooltip: t('T_Comprehensive_desc'), value: tier?.COMP_Coverage_Deductible_Amount },
      { title: t('Q_BI_Bodily_Injury_Liability_YN'), tooltip: t('T_BodilyInjury_desc'), value: tier?.BI_Limit_per_person + '/' + tier?.BI_Limit_per_occurrence },
      { title: t('Q_PD_Property_Damage_Liability_YN'), tooltip: t('T_PropertyDamageLiability_desc'), value: tier?.PD_Limit_per_occurrence },
      { title: t('Q_MP_Medical_Payments_YN'), tooltip: t('T_MedicalPayment_desc'), value: tier?.MP_Limit_per_occurrence },
      { title: t('Q_UBUIBBI_Uninsured_Underinsured_Boater_YN'), tooltip: t('T_Uninsured_Boater_desc'), value: tier?.UBUBBI_Limit_per_occurrence + '/' + tier?.UBUBBI_Limit_per_person },
      { title: t('Q_TL_On_Water_Towing_YN'), tooltip: t('T_OnWaterTowing_desc'), value: tier?.TL_Limit_per_occurrence },
      { title: t('Q_FSHEQ_Fishing_Equipment_YN'), tooltip: t('T_Fishing_Equipment_desc'), value: tier?.FSHEQ_Limit_per_occurrence },
      { title: t('Q_PEREF_Personal_Effects_YN'), tooltip: t('T_Personal_Effects_desc'), value: tier?.PEREF_Limit_per_occurrence },
      { title: t('Q_RSA_Roadside_Assistance'), tooltip: t('T_RoadsideAssistance_desc'), value: tier?.RSA_Roadside_Assistance_YN },
      { title: t('Q_RENTRE_Rental_Reimbursement'), tooltip: t('T_RentalReimbursment_desc'), value: tier?.RENTRE_Rental_Reimbursement_YN },
      ...includedCoverages,
    ];
  
    // the viewConditionQid is the question id that will be used to determine if the row should be added. If checks only if the question is displayed
    const stateSpecificRows: any = {
      'VA': [{
        'addedCoverage': { title: t('Q_UBUIBPD_Limit_YN'), tooltip: t('T_UBUIBPD_Limit_desc'), value: tier?.UBUIBPD_Limit, viewConditionQid: 'UBUIBPD_Limit_YN' },
        'positionToAdd': 6,
      }],
      'MN': [{
        'addedCoverage': { title: t('Q_Family_Bodily_Injury_YN'), tooltip: t('T_Family_Bodily_Injury_desc'), value: tier?.Family_Bodily_Injury_YN, viewConditionQid: 'Family_Bodily_Injury_YN' },
        'positionToAdd': 4,
      }],
    };

    addStateSpecificRows({ stateSpecificRows, rows, calculatedState });
    
    // in addition to the viewConditionQid, the questionValueToAddCoverageOn is the value that the question should have in order to add the row.
    // questionSpecificRows needs to be defined after addStateSpecificRows, because it modifies the rows array and rows.length is used to calculate the positionToAdd
    const questionSpecificRows: any = {
      'is_layup': [{
        'addedCoverage': { title: t('Q_Is_Layup_Coverage'), tooltip: t('D_Layup'), value: 'Included', viewConditionQid: 'is_layup' },
        'positionToAdd': rows.length - 4,//add it last
        'questionValueToAddCoverageOn': '$_BOOL_true',
      }],
    };
    
    // add the rows that are specific to a version
    const versionSpecificRows: any = {
      'V2': [
        {
          'addedCoverage': { title: t('Q_Theft_Deductible_Coverage'), tooltip: '', value: 'Included' },
          'positionToAdd': rows.length - 4,//add it last
        },
        {
          'addedCoverage': { title: t('Q_Lightning_Strike_Deductible_Coverage'), tooltip: '', value: 'Included' },
          'positionToAdd': rows.length - 4,//add it last
        },
        {
          'addedCoverage': { title: t('Q_Storm_Deductible_Coverage'), tooltip: '', value: 'Included' },
          'positionToAdd': rows.length - 4,//add it last
          'dontAddOnStates': ['TX','AL','FL','LA','MS'], // display coverage but put Excluded in the value
        },
      ],
    };

    addQuestionSpecificRows({ questionSpecificRows, rows, questionIdArr: ['is_layup'] });
  
    addVersionSpecificRows({ versionSpecificRows, rows,calculatedState, versionId: this.deps.questionsService.getProgramVersion() });
    addIdProperty({ rows });
    return rows;
  
  };

  private generateIncludedCoveragesMapFromRowsMaster = () => {
    const includedCoveragesMapRaw = this.includedCoverages;
    const includedCoveragesMap: IncludedCoverageItem[] = includedCoveragesMapRaw?.map((el: any) => {
      return {
        text: el?.title,
        spanText: el?.value?.toString(),
        astrixText: el?.astrixText,
      };
    });
    return includedCoveragesMap;
  };

  public getUnderWritingApprovalsData = () => {
    return this.rateResult.get('billing')?.validateUnderWritingApprovalsErrorData?.underwriting_Approvals_Pending_And_Rejected?.underwriting_Approvals_Approved || undefined;
  }

  public includedCoveragesMap = this.generateIncludedCoveragesMapFromRowsMaster();

}

InsuranceService.define();